import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { map } from "lodash";
import { Dropdown, DropdownItemProps, Grid } from "semantic-ui-react";

import { PlayerInfoSection } from "../../../components";
import {
  PlayerAccount,
  socialNetworkNames,
  SocialNetworkPosted,
  SocialNetworksId,
  VideoProjectDetail,
} from "../../../interfaces";
import { PlayersServices } from "../../../services";

import { VideosSection } from "./components/VideosSection";
import { VideoProjectComponentType, descriptors } from "./descriptors";

import { CaptionSection } from "./styled";
import "./styles.scss";
interface OwnProps {
  coachId?: string;
  player?: PlayerAccount;
  videoProject: VideoProjectDetail;
}

type Props = OwnProps;

/**
 * Represents a VideoProjectDetail content.
 */
const VideoProjectDetailClass: React.FC<Props> = ({ videoProject, coachId, player }) => {
  const { formatMessage } = useIntl();

  const { post } = videoProject;

  const postSnNames = post?.socialNetworks.map(sn => sn.socialNetworkId) || [];
  const baseSns: SocialNetworksId[] = ["FB", "LI", "IG", "IG_STORY", "TW", "TT"];
  const snNamesFiltered = baseSns.filter(bsn => postSnNames.includes(bsn));

  const snFiltered: SocialNetworkPosted[] = snNamesFiltered.map(
    bsn => post?.socialNetworks.find(sn => sn.socialNetworkId === bsn)!!
  );
  // Player section
  const [playerInfo, setPlayerInfo] = React.useState<PlayerAccount>();

  const [selectedSocialNetwork, setSelectedSocialNetwork] = React.useState<any | undefined>(
    snFiltered[0]?.socialNetworkId
  );
  const [postedSocialNetworkOptions, setPostedSocialNetworkOptions] = React.useState<DropdownItemProps[]>([]);

  React.useEffect(() => {
    if (coachId) {
      PlayersServices.searchSubscribedAllPlayersByTag(coachId).then(response => {
        const player = response.content.find(p => p.playerAccount.playerId === videoProject.playerId);
        setPlayerInfo(player?.playerAccount);
      });
    } else if (player) {
      setPlayerInfo(undefined);
    }
  }, [coachId, player]);

  React.useEffect(() => {
    return () => {
      window.removeEventListener("loadedmetadata", () => {
        // Nothing
      });
    };
  }, []);

  React.useEffect(() => {
    const filters: DropdownItemProps[] = map(snFiltered, sn => ({
      key: sn.socialNetworkId,
      text: socialNetworkNames[sn.socialNetworkId],
      value: sn.socialNetworkId,
    }));
    setPostedSocialNetworkOptions(filters);
    setSelectedSocialNetwork(filters[0]?.value);
  }, [post?.socialNetworks]);

  const withCaption = React.useMemo(() => {
    return snFiltered.some(sn => sn.caption && sn.caption.length > 0);
  }, [snFiltered]);

  const postedSocialNetworksSection = () => {
    return (
      withCaption && (
        <Dropdown
          fluid
          defaultValue={selectedSocialNetwork}
          selectOnNavigation={false}
          wrapSelection={false}
          selectOnBlur={false}
          onChange={(_, data) => {
            setSelectedSocialNetwork(`${data.value}`);
          }}
          selection
          options={postedSocialNetworkOptions}
        />
      )
    );
  };

  const finalMessage = React.useMemo(() => {
    if (withCaption) {
      const socialPost = snFiltered.find(sn => sn.socialNetworkId === selectedSocialNetwork);
      return socialPost?.caption;
    } else {
      return videoProject.messageToPost;
    }
  }, [selectedSocialNetwork, snFiltered, videoProject.messageToPost, withCaption]);

  const captionSection = (
    <div className="mediumPaddingTop mediumPaddingBottom">
      <div>
        <Grid>
          <Grid.Column className={"noPadding captionSectionHeader"} width={10}>
            <label>{formatMessage({ ...descriptors[VideoProjectComponentType.message] })}</label>
          </Grid.Column>
          {withCaption && (
            <Grid.Column width={5} className={"noPadding"}>
              {postedSocialNetworksSection()}
            </Grid.Column>
          )}
        </Grid>
      </div>
      <CaptionSection data-elm-id="videoProjectDetailCaption">{finalMessage}</CaptionSection>
    </div>
  );

  return (
    <div className={"videoProjectDetailContainer"}>
      {/* PLAYER INFO */}
      {playerInfo && (
        <>
          <Grid.Row>
            <label>
              <FormattedMessage {...descriptors[VideoProjectComponentType.playerSection]} />
            </label>
          </Grid.Row>
          <PlayerInfoSection playerInfo={playerInfo} showPhone={true} />
        </>
      )}

      {/* Project Files section */}
      <Grid.Row className={player ? "" : "sectionHeader"}>
        <label>
          <FormattedMessage {...descriptors[VideoProjectComponentType.projectFilesTitle]} />
        </label>
      </Grid.Row>

      {/* Files section */}
      {videoProject.editedMedia && (
        <VideosSection
          title={formatMessage({ ...descriptors[VideoProjectComponentType.editedVideosDesc] })}
          type="EDITED"
          videos={videoProject.editedMedia?.media!!}
          videoProjectId={videoProject.videoProjectId!!}
          variant="original"
          cols={2}
        />
      )}
      {videoProject.originalMedia && (
        <VideosSection
          title={formatMessage({ ...descriptors[VideoProjectComponentType.originalVideosDesc] })}
          type="ORIGINAL"
          videos={videoProject.originalMedia?.media!!}
          videoProjectId={videoProject.videoProjectId!!}
          hideable={!!videoProject.editedMedia}
        />
      )}

      {captionSection}
    </div>
  );
};

export const VideoProjectDetailComponent = VideoProjectDetailClass;
